<script setup lang="ts">
// a person opened from anywhere but the people list (see
// `composables/use-person-overlay.ts`): the same modal as
// `pages/people/[slug].vue`, stacked on the current page instead of on the
// list, and without touching the url. Mounted once in `app.vue`.
const slug = usePersonOverlaySlug()

const route = useRoute()
const localeRoute = useLocaleRoute()
const { locale } = useI18n()

const runtimeConfig = useRuntimeConfig()
const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

// only ever opened by a click, so the person is fetched on the client when
// the slug is set. The modal appears once the data is there, as the route
// version does
const data = ref<any>(null)

watch(slug, async (value) => {
  data.value = null
  if (!value) return

  let person: any = null
  try {
    const result = await $fetch('/api/payload-gql', {
      method: 'POST',
      body: {
        query: SinglePersonQuery,
        variables: {
          slug: { equals: value },
          locale: locale.value,
          isPreview: isPreview.value,
          status: isPreview.value ? 'draft' : 'published',
        },
        isPreview: isPreview.value,
      },
    })
    person = transformPayloadData<any>(result)
  } catch {
    person = null
  }

  // another person was opened (or this one closed) while loading
  if (slug.value !== value) return

  if (person) {
    data.value = person
    return
  }

  // nothing to show in the overlay: fall through to the route, which handles
  // the 404 like any other page
  slug.value = null
  navigateTo(localeRoute({ name: 'people-slug', params: { slug: value } }))
})

</script>

<template>
  <page-modal
    v-if="slug && data"
    v-slot="{ close }"
    @close="slug = null"
  >
    <pages-people-slug-panel :data="data">
      <template #close>
        <button
          type="button"
          class="group icon-box-cross"
          :aria-label="$t('labels.close')"
          @click="close"
        >
          <svgo-cross class="w-full overflow-visible stroke-black stroke-default group-hover:stroke-accent icon-hover" />
        </button>
      </template>
    </pages-people-slug-panel>
  </page-modal>
</template>
